import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { ContentHeader, Field, QueryStatus, useToast } from "@cx/ui";
import type {
  FinancialAccountView,
  PaymentMethodView,
  PixProviderId,
  PixSettingsView,
  PixWebhookHealth,
} from "@cx/contracts";
import { can, type Permissions } from "@cx/db";
import { ApiFailure } from "../../api";
import { errorMessage } from "../../errors";
import { useSession } from "../../session";
import {
  DEFAULT_PROVIDER,
  EXPIRATION_OPTIONS,
  KEY_KIND_LABEL,
  PROVIDER_LABEL,
  providerOptions,
  webhookHealthWarning,
  webhookHint,
} from "./labels";

type SettingsResponse = {
  settings: PixSettingsView | null;
  sandboxAllowed: boolean;
  /** true/false = tentou cadastrar o webhook no provedor; null = o webhook não mudou. */
  webhookRegistered?: boolean | null;
  /** Fila do webhook no provedor (Asaas) e notificações fora do formato. */
  webhookHealth?: PixWebhookHealth | null;
};

type Values = {
  provider: PixProviderId;
  active: boolean;
  pixKey: string;
  merchantName: string;
  merchantCity: string;
  financialAccountId: string;
  paymentMethodId: string;
  expirationSeconds: number;
  clientId: string;
  clientSecret: string;
  certificateBase64: string;
  environment: "homologation" | "production";
  apiKey: string;
  asaasEnvironment: "sandbox" | "production";
  notifyEmail: string;
  rotateWebhookSecret: boolean;
};

const empty = (): Values => ({
  provider: DEFAULT_PROVIDER,
  active: true,
  pixKey: "",
  merchantName: "",
  merchantCity: "",
  financialAccountId: "",
  paymentMethodId: "",
  expirationSeconds: 86_400,
  clientId: "",
  clientSecret: "",
  certificateBase64: "",
  environment: "homologation",
  apiKey: "",
  asaasEnvironment: "sandbox",
  notifyEmail: "",
  rotateWebhookSecret: false,
});

/** Credenciais novas no formato do provedor escolhido; nada preenchido = mantém as gravadas (null). */
function credentialsBody(x: Values) {
  if (x.provider === "asaas") return x.apiKey ? { apiKey: x.apiKey, environment: x.asaasEnvironment } : null;
  if (x.provider === "efi" && (x.clientId || x.clientSecret || x.certificateBase64))
    return {
      clientId: x.clientId,
      clientSecret: x.clientSecret,
      certificateBase64: x.certificateBase64,
      environment: x.environment,
    };
  return null;
}

/** Lê o .p12 como base64 (vai cifrado para o servidor e nunca volta). */
const fileToBase64 = (f: File) =>
  f.arrayBuffer().then((b) => {
    let s = "";
    for (const byte of new Uint8Array(b)) s += String.fromCharCode(byte);
    return btoa(s);
  });

/** Preferências › Integrações › Pix: provedor, chave, recebedor, conta de destino da baixa automática, credenciais. */
export function PixSettingsPage() {
  const { companyId, me, api } = useSession();
  const perms = me.permissions as Permissions;
  const canUpdate = can(perms, "payment_integrations", "update");
  const qc = useQueryClient();
  const toast = useToast();
  const key = ["pix-settings", companyId];
  const settings = useQuery({ queryKey: key, queryFn: () => api<SettingsResponse>("/pix-settings") });
  const accounts = useQuery({
    queryKey: ["fin-accounts", companyId, "pix-options"],
    enabled: canUpdate,
    queryFn: () => api<{ data: FinancialAccountView[] }>("/financial-accounts"),
  });
  const methods = useQuery({
    queryKey: ["payment-methods", companyId, "pix-options"],
    enabled: canUpdate,
    queryFn: () => api<{ data: PaymentMethodView[] }>("/payment-methods"),
  });
  const [v, setV] = useState<Values | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const set = <K extends keyof Values>(k: K, val: Values[K]) => setV((x) => (x ? { ...x, [k]: val } : x));
  const data = settings.data;
  useEffect(() => {
    if (!data) return;
    const s = data.settings;
    setV(
      s
        ? {
            ...empty(),
            provider: s.provider,
            active: s.active,
            pixKey: s.pixKey,
            merchantName: s.merchantName,
            merchantCity: s.merchantCity,
            financialAccountId: s.financialAccountId,
            paymentMethodId: s.paymentMethodId ?? "",
            expirationSeconds: s.expirationSeconds,
            notifyEmail: s.notifyEmail ?? "",
          }
        : empty(),
    );
  }, [data]);
  const save = useMutation({
    mutationFn: () => {
      const x = v!;
      return api<SettingsResponse>("/pix-settings", {
        method: "PUT",
        body: JSON.stringify({
          provider: x.provider,
          active: x.active,
          pixKey: x.pixKey,
          merchantName: x.merchantName,
          merchantCity: x.merchantCity,
          financialAccountId: x.financialAccountId,
          paymentMethodId: x.paymentMethodId || null,
          expirationSeconds: x.expirationSeconds,
          credentials: credentialsBody(x),
          notifyEmail: x.notifyEmail.trim() || null,
          rotateWebhookSecret: x.rotateWebhookSecret,
        }),
      });
    },
    onSuccess: (r) => {
      setErrors({});
      qc.setQueryData(key, r);
      setV((x) =>
        x
          ? {
              ...x,
              clientId: "",
              clientSecret: "",
              certificateBase64: "",
              apiKey: "",
              rotateWebhookSecret: false,
            }
          : x,
      );
      toast(
        r.webhookRegistered === false ? "warning" : "success",
        r.webhookRegistered === false
          ? "Configuração salva, mas o webhook não foi cadastrado no provedor. Salve de novo com “Gerar novo segredo do webhook”."
          : "Configuração do Pix salva",
      );
    },
    onError: (e) => {
      if (e instanceof ApiFailure && e.fields) setErrors(e.fields);
      else if (e instanceof ApiFailure && e.code === "invalid_pix_key") setErrors({ pixKey: e.message });
      toast("danger", errorMessage(e));
    },
  });
  const current = data?.settings;
  const healthWarning = current ? webhookHealthWarning(current.provider, data?.webhookHealth) : null;
  return (
    <>
      <ContentHeader
        title="Integrações · Pix"
        breadcrumb={["Preferências", "Integrações", "Pix"]}
        actions={
          canUpdate && v ? (
            <button
              type="button"
              className="btn btn-primary"
              disabled={save.isPending}
              onClick={() => save.mutate()}
            >
              Salvar
            </button>
          ) : undefined
        }
      />
      <QueryStatus loading={settings.isLoading} error={settings.error} />
      {v && data && (
        <form
          className="box card p-3"
          onSubmit={(e) => {
            e.preventDefault();
            if (canUpdate) save.mutate();
          }}
        >
          <fieldset disabled={!canUpdate}>
            {healthWarning && (
              <div className="alert alert-warning py-2" role="alert">
                {healthWarning}
              </div>
            )}
            {!current && (
              <div className="alert alert-info py-2">
                Configure o Pix para gerar cobranças nas contas a receber. Os pagamentos recebidos são
                baixados automaticamente na conta escolhida abaixo.
              </div>
            )}
            <div className="row">
              <Field label="Provedor" required className="col-md-6" error={errors.provider}>
                <select
                  className="form-select"
                  value={v.provider}
                  onChange={(e) => set("provider", e.target.value as PixProviderId)}
                >
                  {providerOptions(data.sandboxAllowed, v.provider).map((p) => (
                    <option key={p} value={p}>
                      {PROVIDER_LABEL[p]}
                    </option>
                  ))}
                </select>
              </Field>
              <div className="col-md-6 mb-3 d-flex align-items-end">
                <div className="form-check">
                  <input
                    id="pix-active"
                    type="checkbox"
                    className="form-check-input"
                    checked={v.active}
                    onChange={(e) => set("active", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="pix-active">
                    Gerar cobranças Pix (ativo)
                  </label>
                </div>
              </div>
              <Field
                label="Chave Pix"
                required
                className="col-md-6"
                error={errors.pixKey}
                hint={
                  current
                    ? `Tipo: ${KEY_KIND_LABEL[current.pixKeyKind]}`
                    : "CPF, CNPJ, e-mail, celular (+55 DDD número) ou chave aleatória"
                }
              >
                <input value={v.pixKey} maxLength={100} onChange={(e) => set("pixKey", e.target.value)} />
              </Field>
              <Field label="Validade padrão" className="col-md-6" error={errors.expirationSeconds}>
                <select
                  className="form-select"
                  value={v.expirationSeconds}
                  onChange={(e) => set("expirationSeconds", Number(e.target.value))}
                >
                  {EXPIRATION_OPTIONS.map((o) => (
                    <option key={o.seconds} value={o.seconds}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field
                label="Nome do recebedor"
                required
                className="col-md-6"
                error={errors.merchantName}
                hint="Até 25 letras, sem acentos (vai no QR Code)"
              >
                <input
                  value={v.merchantName}
                  maxLength={60}
                  onChange={(e) => set("merchantName", e.target.value)}
                />
              </Field>
              <Field
                label="Cidade do recebedor"
                required
                className="col-md-6"
                error={errors.merchantCity}
                hint="Até 15 letras, sem acentos"
              >
                <input
                  value={v.merchantCity}
                  maxLength={60}
                  onChange={(e) => set("merchantCity", e.target.value)}
                />
              </Field>
              <Field
                label="Conta de destino"
                required
                className="col-md-6"
                error={errors.financialAccountId}
                hint="A baixa automática lança o recebimento nesta conta"
              >
                <select
                  className="form-select"
                  value={v.financialAccountId}
                  onChange={(e) => set("financialAccountId", e.target.value)}
                >
                  <option value="">Escolha</option>
                  {current && !accounts.data?.data.some((a) => a.id === current.financialAccountId) && (
                    <option value={current.financialAccountId}>
                      {current.financialAccountName ?? "Conta atual"}
                    </option>
                  )}
                  {(accounts.data?.data ?? [])
                    .filter((a) => a.active || a.id === v.financialAccountId)
                    .map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                </select>
              </Field>
              <Field
                label="Forma de pagamento"
                className="col-md-6"
                error={errors.paymentMethodId}
                hint="Opcional: a taxa dela é descontada na baixa"
              >
                <select
                  className="form-select"
                  value={v.paymentMethodId}
                  onChange={(e) => set("paymentMethodId", e.target.value)}
                >
                  <option value="">Nenhuma</option>
                  {(methods.data?.data ?? [])
                    .filter((m) => m.active || m.id === v.paymentMethodId)
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.name}
                      </option>
                    ))}
                </select>
              </Field>
            </div>
            {v.provider === "asaas" && (
              <>
                <h2 className="h6 mt-2">Credenciais do Asaas</h2>
                <p className="text-muted small">
                  {current?.provider === "asaas" && current.hasCredentials
                    ? "Chave de API gravada (não é exibida). Preencha só para trocar."
                    : "Chave de API gerada no Asaas em Integrações › Chaves de API (a do sandbox começa com $aact_hmlg_, a de produção com $aact_prod_)."}{" "}
                  O Asaas exige o CPF ou CNPJ do cliente para gerar o Pix.
                </p>
                <div className="row">
                  <Field label="Chave de API" className="col-md-6" error={errors["credentials.apiKey"]}>
                    <input
                      type="password"
                      value={v.apiKey}
                      autoComplete="new-password"
                      onChange={(e) => set("apiKey", e.target.value)}
                    />
                  </Field>
                  <Field
                    label="Ambiente do Asaas"
                    className="col-md-6"
                    error={errors["credentials.environment"]}
                  >
                    <select
                      className="form-select"
                      value={v.asaasEnvironment}
                      onChange={(e) => set("asaasEnvironment", e.target.value as Values["asaasEnvironment"])}
                    >
                      <option value="sandbox">Sandbox (testes)</option>
                      <option value="production">Produção</option>
                    </select>
                  </Field>
                  <Field
                    label="E-mail de contato para avisos do webhook"
                    required
                    className="col-md-6"
                    error={errors.notifyEmail}
                    hint="O Asaas avisa neste e-mail se a fila de notificações parar. Em branco = e-mail da empresa."
                  >
                    <input
                      type="email"
                      value={v.notifyEmail}
                      maxLength={254}
                      autoComplete="email"
                      onChange={(e) => set("notifyEmail", e.target.value)}
                    />
                  </Field>
                </div>
              </>
            )}
            {v.provider === "efi" && (
              <>
                <h2 className="h6 mt-2">Credenciais do Efí</h2>
                <p className="text-muted small">
                  {current?.provider === "efi" && current.hasCredentials
                    ? "Credenciais gravadas (não são exibidas). Preencha só para trocar."
                    : "Client ID, Client Secret e certificado .p12 da aplicação criada no painel do Efí."}
                </p>
                <div className="row">
                  <Field label="Client ID" className="col-md-6" error={errors["credentials.clientId"]}>
                    <input
                      value={v.clientId}
                      autoComplete="off"
                      onChange={(e) => set("clientId", e.target.value)}
                    />
                  </Field>
                  <Field
                    label="Client Secret"
                    className="col-md-6"
                    error={errors["credentials.clientSecret"]}
                  >
                    <input
                      type="password"
                      value={v.clientSecret}
                      autoComplete="new-password"
                      onChange={(e) => set("clientSecret", e.target.value)}
                    />
                  </Field>
                  <Field
                    label="Certificado (.p12)"
                    className="col-md-6"
                    error={errors["credentials.certificateBase64"]}
                  >
                    <input
                      type="file"
                      accept=".p12,.pfx,application/x-pkcs12"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        set("certificateBase64", f ? await fileToBase64(f) : "");
                      }}
                    />
                  </Field>
                  <Field label="Ambiente" className="col-md-6" error={errors["credentials.environment"]}>
                    <select
                      className="form-select"
                      value={v.environment}
                      onChange={(e) => set("environment", e.target.value as Values["environment"])}
                    >
                      <option value="homologation">Homologação</option>
                      <option value="production">Produção</option>
                    </select>
                  </Field>
                </div>
              </>
            )}
            {current && (
              <>
                <p className="text-muted small">
                  Webhook de pagamentos:{" "}
                  <code>{webhookHint(current.provider, current.webhookPath).path}</code> (
                  {webhookHint(current.provider, current.webhookPath).note})
                </p>
                <div className="form-check">
                  <input
                    id="pix-rotate"
                    type="checkbox"
                    className="form-check-input"
                    checked={v.rotateWebhookSecret}
                    onChange={(e) => set("rotateWebhookSecret", e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="pix-rotate">
                    Gerar novo segredo do webhook ao salvar (o atual deixa de valer e o endereço é cadastrado
                    de novo no provedor)
                  </label>
                </div>
              </>
            )}
          </fieldset>
        </form>
      )}
    </>
  );
}
